import Luiv3Constants from '@app/lui3/constants';
import styled from 'styled-components';

import BtnContainer from '../Btn/BtnContainer';
import {SvgIcon} from '../Icon';
import {InputGroupContainer} from '../InputGroup';
import Label from '../Label';
import {colorVar} from '../Theme/colors';
import {tintVar} from '../Theme/tints';

export const InlineInputLabel = styled.div`
  margin-block: calc(-1 * var(--input-block-padding));
  min-width: 4rem;
  font-weight: var(--theme-font-weight-control);
  color: var(--input-label-text-color);
  opacity: 0.5;
`;

export const LengthIndicator = styled.div`
  margin-block: calc(-1 * var(--input-block-padding));
  font-weight: var(--theme-font-weight-control);
  color: var(--input-label-text-color);
  opacity: 0.5;
`;

export const SupportingText = styled.div`
  position: absolute;
  top: 100%;
  line-height: 1;
  inset-inline: 0;
  font-size: var(--theme-font-size-tiny);
  padding-block: var(--theme-sub-primitive-gap);
  white-space: nowrap;
  overflow-x: hidden;
  text-overflow: ellipsis;
`;

export type Style = 'outline' | 'filled' | 'seamless' | 'secondary' | 'slim';
export type Size = 'small' | 'default' | 'compact' | 'big' | 'huge';

const InputWrapper = styled(Label)`
  input,
  textarea {
    background: none;
    outline: none;
    border: none;
    margin: 0;
    padding: 0;
    flex: 1 1 auto;
    min-width: 0;

    ::placeholder {
      color: ${Luiv3Constants.baseColor['gray-400']};
    }
  }

  input {
    height: var(--input-control-size);
  }

  textarea {
    resize: block;
    padding-block: calc(
      (var(--input-control-size) - var(--input-font-size)) / 2
    );
  }

  flex-grow: 1;

  --input-size: var(--input-default-size, 2.5rem);
  --input-control-size: calc(var(--input-size) * 0.75 - 0.125rem);
  --input-font-size: var(--theme-font-size-control);

  --badge-default-size: var(--input-control-size);
  --badge-default-padding: calc(var(--input-control-size) / 2 - 0.375rem);

  &[data-size='small'] {
    --input-size: 1.5rem;
  }
  &[data-size='compact'] {
    --input-size: 2rem;
  }
  &[data-size='big'] {
    --input-size: 3.125rem;
  }
  &[data-size='huge'] {
    --input-size: 5.25rem;
  }

  --input-base-input-padding: 0.625rem;

  --input-gap: 0.25rem;
  --input-inline-padding: var(--input-base-input-padding);
  --input-block-padding: calc(
    (var(--input-size) - var(--input-control-size)) / 2
  );

  &[data-reduced-padding] {
    --input-inline-padding: var(--input-block-padding);
    input,
    textarea {
      padding-inline: calc(
        var(--input-base-input-padding) - var(--input-inline-padding)
      );
    }
  }

  padding-inline: var(--input-inline-padding);
  padding-block: var(--input-block-padding);
  font-size: var(--input-font-size);
  min-height: var(--input-size);
  display: flex;
  align-items: center;
  gap: var(--input-gap);

  border-radius: var(--theme-radius);

  &[data-radius='square'] {
    border-radius: 0;
  }
  &[data-radius='small'] {
    border-radius: var(--theme-radius-sm);
  }
  &[data-radius='normal'] {
    border-radius: var(--theme-radius);
  }

  &[data-square='true'] {
    border-radius: 0;
  }

  border: none;
  contain: layout;
  overflow: hidden;

  &[data-padding='slim'] {
    padding-inline: calc(var(--input-inline-padding) / 2);
    padding-block: calc(var(--input-block-padding) / 2);
  }

  background-color: color-mix(
    in oklab,
    currentColor calc(var(--background-interaction-tint, 0) * 100%),
    color-mix(
      in oklab,
      currentColor calc(var(--background-tint) * 100%),
      var(--theme-color-surface)
    )
  );

  ${InputGroupContainer} > :not(:last-child) & {
    border-start-end-radius: 0;
    border-end-end-radius: 0;
  }

  ${InputGroupContainer} > :not(:first-child) & {
    border-start-start-radius: 0;
    border-end-start-radius: 0;
  }

  --icon-default-size: 1.25rem;

  :has(:disabled),
  &:disabled {
    opacity: 0.38;
    user-select: none;
    pointer-events: none;
  }

  &[data-readonly='true'] {
    pointer-events: none;
    user-select: none;
    cursor: default;
  }

  &[data-readonly='true'][data-style='outline'] {
    border: none;
  }

  &[data-state='error'] {
    ${SupportingText} {
      color: ${colorVar('error')};
    }
  }
  &[data-state='warning'] {
    ${SupportingText} {
      color: ${colorVar('warn')};
    }
  }

  &[data-style='seamless'] {
    --input-inline-padding: 0;
    background: none;
  }

  &[data-style='secondary'] {
    --background-tint: ${tintVar('elevation-2')};
    background-color: #eef0f3;
  }

  &[data-style='filled'] {
    background-color: ${Luiv3Constants.baseColor['gray-100']};
    :has(:disabled),
    &:disabled {
      background-color: color-mix(
        in oklab,
        transparent,
        ${colorVar('onSurface')} 10%
      );
    }

    :not(:has(:disabled)) {
      :hover,
      &[data-force-hover='true'] {
        --background-interaction-tint: ${tintVar('hover')};
      }
    }
  }

  &[data-style='outline'] {
    border: solid var(--theme-border-thickness) ${colorVar('outline')};
    background-color: ${colorVar('surface')};

    --input-block-padding: calc(
      ((var(--input-size) - var(--input-control-size)) / 2) - var(
          --theme-border-thickness
        )
    );

    :has(:disabled),
    &:disabled {
      background-color: color-mix(
        in oklab,
        transparent,
        ${colorVar('surface')} 32%
      );
      border-color: color-mix(
        in oklab,
        transparent,
        ${colorVar('onSurface')} 32%
      );
    }

    :not(:has(:disabled)) {
      :hover,
      &[data-force-hover='true'] {
        border-color: ${Luiv3Constants.baseColor['gray-400']};
      }
      :focus-within {
        outline: solid var(--theme-border-thickness) ${colorVar('primary')};
        outline-offset: calc(-1 * var(--theme-border-thickness));
      }

      &[data-state='error'] {
        border-color: ${colorVar('error')};
        :hover,
        &[data-force-hover='true'] {
          border-color: ${colorVar('onErrorContainer')};
        }
        :focus-within {
          outline-color: ${colorVar('error')};
        }
      }

      &[data-state='warning'] {
        border-color: ${colorVar('warn')};
        :hover,
        &[data-force-hover='true'] {
          border-color: ${colorVar('onWarnContainer')};
        }
        :focus-within {
          outline-color: ${colorVar('warn')};
        }
      }
    }
  }

  ${SvgIcon} {
    margin-block: calc(-1 * var(--input-block-padding));
    :first-child {
      margin-inline-start: calc(-1 / 5 * var(--input-inline-padding));
    }
    :last-child {
      margin-inline-end: calc(-1 / 5 * var(--input-inline-padding));
    }
  }

  ${BtnContainer} {
    margin-block: calc(-1 * var(--input-block-padding));
    :first-child {
      margin-inline-start: calc(-1 * var(--input-inline-padding));
    }
    :last-child {
      margin-inline-end: calc(-1 * var(--input-inline-padding));
    }
  }

  & .no-padding {
    margin-block: calc(-1 * var(--input-block-padding));
    :first-child {
      margin-inline-start: calc(-1 * var(--input-inline-padding));
    }
    :last-child {
      margin-inline-end: calc(-1 * var(--input-inline-padding));
    }
  }

  &[data-bold='true'] {
    input,
    textarea {
      font-weight: var(--theme-font-weight-emphasize);
    }
  }
`;

export default InputWrapper;
